"use strict";
/*******************************************************************************

    Contains a class that builds transactions of a wallet with multiple senders
        and multiple receivers.

    Copyright:
        Copyright (c) 2021 BOSAGORA Foundation
        All rights reserved.

    License:
        MIT License. See LICENSE for details.

*******************************************************************************/
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WalletCancelBuilder = exports.WalletUnfreezeBuilder = exports.AccountUTXOSummary = exports.WalletTxBuilderSingleReceiver = exports.WalletTxBuilder = exports.WalletSenderContainer = exports.WalletReceiverContainer = exports.WalletSender = exports.WalletReceiver = void 0;
var Amount_1 = require("../common/Amount");
var ECC_1 = require("../common/ECC");
var Hash_1 = require("../common/Hash");
var KeyPair_1 = require("../common/KeyPair");
var Constant_1 = require("../data/Constant");
var Transaction_1 = require("../data/Transaction");
var TxInput_1 = require("../data/TxInput");
var TxOutput_1 = require("../data/TxOutput");
var Lock_1 = require("../script/Lock");
var TxBuilder_1 = require("../utils/TxBuilder");
var TxCanceller_1 = require("../utils/TxCanceller");
var TxPayloadFee_1 = require("../utils/TxPayloadFee");
var Utils_1 = require("../utils/Utils");
var Account_1 = require("./Account");
var EventDispatcher_1 = require("./EventDispatcher");
var Types_1 = require("./Types");
var WalletUtil_1 = require("./WalletUtil");
var jsbi_1 = __importDefault(require("jsbi"));
/**
 * Definition of a class with elements to be used as an output of a transaction
 */
var WalletReceiver = /** @class */ (function () {
    /**
     * Constructor
     * @param address The address of the receiver
     * @param amount  The amount to transfer
     */
    function WalletReceiver(address, amount) {
        this.address = address;
        this.amount = amount;
    }
    return WalletReceiver;
}());
exports.WalletReceiver = WalletReceiver;
/**
 * Definition of a class with elements to be used as a input of a transaction
 */
var WalletSender = /** @class */ (function () {
    /**
     * Constructor
     * @param account The instance of Account. It has the account's public key, secret key, and balance information.
     */
    function WalletSender(account) {
        this.account = account;
        this.drawn = Amount_1.Amount.make(0);
        this.remaining = Amount_1.Amount.make(0);
        this.spendable = Amount_1.Amount.make(this.account.balance.spendable);
        this.utxos = [];
        this.total_amount_utxos = Amount_1.Amount.make(0);
        this.enable = false;
    }
    WalletSender.prototype.calculateUTXOSum = function () {
        this.total_amount_utxos = this.utxos.reduce(function (prev, value) { return Amount_1.Amount.add(prev, value.amount); }, Amount_1.Amount.make(0));
    };
    return WalletSender;
}());
exports.WalletSender = WalletSender;
/**
 * The container for WalletReceiver.
 */
var WalletReceiverContainer = /** @class */ (function (_super) {
    __extends(WalletReceiverContainer, _super);
    function WalletReceiverContainer() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        /**
         * The storage of the receivers
         */
        _this.items = [];
        return _this;
    }
    /**
     * Add a receiver
     * @param receiver  the receiver to be added
     * @param replace   Specify whether to change the amount or ignore it when there is one with the same address.
     * If this value is true, if the same account address exists, it will be changed to a new amount.
     */
    WalletReceiverContainer.prototype.add = function (receiver, replace) {
        if (replace === void 0) { replace = true; }
        if (replace) {
            var elem = this.items.find(function (m) { return KeyPair_1.PublicKey.equal(m.address, receiver.address); });
            if (elem !== undefined)
                elem.amount = receiver.amount;
            else
                this.items.push(new WalletReceiver(receiver.address, receiver.amount));
        }
        else {
            this.items.push(new WalletReceiver(receiver.address, receiver.amount));
        }
        return true;
    };
    /**
     * Remove a receiver
     * @param receiver the receiver to be removed
     */
    WalletReceiverContainer.prototype.remove = function (receiver) {
        var idx = this.items.findIndex(function (m) { return KeyPair_1.PublicKey.equal(m.address, receiver.address) && Amount_1.Amount.equal(m.amount, receiver.amount); });
        if (idx < 0)
            return false;
        this.items.splice(idx, 1);
        return true;
    };
    /**
     * If there's an element with a public key, return true, otherwise return false.
     * @param address The public key
     */
    WalletReceiverContainer.prototype.exist = function (address) {
        return this.items.findIndex(function (m) { return KeyPair_1.PublicKey.equal(m.address, address); }) >= 0;
    };
    /**
     * Remove a receiver by address
     * @param address the receiver to be removed with this address
     */
    WalletReceiverContainer.prototype.removeAddress = function (address) {
        var changed = false;
        while (true) {
            var idx = this.items.findIndex(function (m) { return KeyPair_1.PublicKey.equal(m.address, address); });
            if (idx < 0)
                break;
            this.items.splice(idx, 1);
            changed = true;
        }
        return changed;
    };
    /**
     * Remove all receivers
     */
    WalletReceiverContainer.prototype.clear = function () {
        var changed = this.items.length > 0;
        this.items.length = 0;
        return changed;
    };
    Object.defineProperty(WalletReceiverContainer.prototype, "length", {
        /**
         * Return the number of receivers
         */
        get: function () {
            return this.items.length;
        },
        enumerable: false,
        configurable: true
    });
    return WalletReceiverContainer;
}(EventDispatcher_1.EventDispatcher));
exports.WalletReceiverContainer = WalletReceiverContainer;
/**
 * The container for several WalletSender.
 */
var WalletSenderContainer = /** @class */ (function (_super) {
    __extends(WalletSenderContainer, _super);
    function WalletSenderContainer() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        /**
         * The storage of the senders
         */
        _this.items = [];
        return _this;
    }
    /**
     * Add a sender
     * @param account The account of the sender to be added
     * @param drawn The amount to be withdrawn.
     */
    WalletSenderContainer.prototype.add = function (account, drawn) {
        var elem = this.items.find(function (m) { return KeyPair_1.PublicKey.equal(m.account.address, account.address); });
        if (elem !== undefined)
            return undefined;
        var sender = new WalletSender(account);
        sender.drawn = Amount_1.Amount.make(drawn);
        this.items.push(sender);
        return sender;
    };
    /**
     * Remove a sender
     * @param account The account of the sender to be removed
     */
    WalletSenderContainer.prototype.remove = function (account) {
        var idx = this.items.findIndex(function (m) { return KeyPair_1.PublicKey.equal(m.account.address, account.address); });
        if (idx < 0)
            return undefined;
        var sender = this.items[idx];
        this.items.splice(idx, 1);
        return sender;
    };
    /**
     * If there's an element with a public key, return true, otherwise return false.
     * @param address The public key
     */
    WalletSenderContainer.prototype.exist = function (address) {
        return this.items.findIndex(function (m) { return KeyPair_1.PublicKey.equal(m.account.address, address); }) >= 0;
    };
    /**
     * Clear all sender
     */
    WalletSenderContainer.prototype.clear = function () {
        var changed = this.items.length > 0;
        this.items.length = 0;
        return changed;
    };
    Object.defineProperty(WalletSenderContainer.prototype, "length", {
        /**
         * Return the number of the senders
         */
        get: function () {
            return this.items.length;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletSenderContainer.prototype, "readonly_accounts", {
        /**
         * Return the senders without the secret key
         */
        get: function () {
            return this.items.filter(function (m) { return m.account.mode === Account_1.AccountMode.READ_ONLY; }).map(function (m) { return m.account; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletSenderContainer.prototype, "sudo_accounts", {
        /**
         * Return the senders with the secret key
         */
        get: function () {
            return this.items.filter(function (m) { return m.account.mode === Account_1.AccountMode.SUDO; }).map(function (m) { return m.account; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletSenderContainer.prototype, "accounts", {
        /**
         * Return the array of account
         */
        get: function () {
            return this.items.map(function (m) { return m.account; });
        },
        enumerable: false,
        configurable: true
    });
    WalletSenderContainer.prototype.values = function () {
        return this.items.map(function (m) {
            return {
                address: m.account.address,
                drawn: m.drawn,
                remaining: m.remaining,
                spendable: m.spendable,
            };
        });
    };
    return WalletSenderContainer;
}(EventDispatcher_1.EventDispatcher));
exports.WalletSenderContainer = WalletSenderContainer;
/**
 * The transaction builder for multi sender, multi receiver
 */
var WalletTxBuilder = /** @class */ (function (_super) {
    __extends(WalletTxBuilder, _super);
    /**
     * Constructor
     * @param client The wallet client to request
     */
    function WalletTxBuilder(client) {
        var _this = _super.call(this) || this;
        _this._client = client;
        _this._receivers = new WalletReceiverContainer();
        _this._senders = new WalletSenderContainer();
        _this._fee_option = Types_1.WalletTransactionFeeOption.Medium;
        _this._fee_tx = Amount_1.Amount.make(0);
        _this._fee_payload = Amount_1.Amount.make(0);
        _this._fee_freezing = Amount_1.Amount.make(0);
        _this._payload = Buffer.alloc(0);
        _this._output_type = TxOutput_1.OutputType.Payment;
        _this._total_spendable = Amount_1.Amount.make(0);
        _this._total_drawn = Amount_1.Amount.make(0);
        _this._remaining = Amount_1.Amount.make(0);
        _this._fee_rate = Utils_1.Utils.FEE_RATE;
        _this._size_tx = Transaction_1.Transaction.getEstimatedNumberOfBytes(1, 2, 0);
        _this._fee_tx = _this.getEstimatedFee(1, 2, 0);
        _this._latest_fee_rate_time = 0;
        _this._manual_fee = false;
        _this._already_change_fee = false;
        _this._client.getTransactionFee(_this._size_tx).then(function (res) {
            if (res.code === Types_1.WalletResultCode.Success && res.data !== undefined) {
                _this._fee_rate = jsbi_1.default.toNumber(Amount_1.Amount.divide(res.data.getFee(_this._fee_option), _this._size_tx).value);
                if (_this._fee_rate < Utils_1.Utils.FEE_RATE)
                    _this._fee_rate = Utils_1.Utils.FEE_RATE;
                _this._fee_tx = _this.getEstimatedFee(1, 2, 0);
            }
            else {
                _this._fee_rate = Utils_1.Utils.FEE_RATE;
            }
        });
        return _this;
    }
    /**
     *
     * @param value
     */
    WalletTxBuilder.prototype.setOutputType = function (value) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this._output_type = value;
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     *
     */
    WalletTxBuilder.prototype.getOutputType = function () {
        return this._output_type;
    };
    /**
     * Add a receiver
     * @param receiver  the receiver to be added
     * @param replace   Specify whether to change the amount or ignore it when there is one with the same address.
     * If this value is true, if the same account address exists, it will be changed to a new amount.
     */
    WalletTxBuilder.prototype.addReceiver = function (receiver, replace) {
        if (replace === void 0) { replace = true; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this._receivers.add(receiver, replace)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_RECEIVER);
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Remove a receiver
     * @param receiver the receiver to be removed
     */
    WalletTxBuilder.prototype.removeReceiver = function (receiver) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this._receivers.remove(receiver)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_RECEIVER);
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Remove a receiver by address
     * @param address
     */
    WalletTxBuilder.prototype.removeReceiverAddress = function (address) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this._receivers.removeAddress(address)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_RECEIVER);
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Remove all receivers
     */
    WalletTxBuilder.prototype.clearReceiver = function (is_dispatch) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this._receivers.clear()) return [3 /*break*/, 2];
                        if (!is_dispatch) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_RECEIVER);
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(WalletTxBuilder.prototype, "lengthReceiver", {
        /**
         * Return the number of receivers
         */
        get: function () {
            return this._receivers.length;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Add a sender
     * @param account The account of the sender to be added
     * @param drawn The amount to be withdrawn.
     */
    WalletTxBuilder.prototype.addSender = function (account, drawn) {
        return __awaiter(this, void 0, void 0, function () {
            var sender, res;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        sender = this._senders.add(account, drawn);
                        if (!(sender !== undefined)) return [3 /*break*/, 6];
                        if (!!sender.account.balance.enable) return [3 /*break*/, 2];
                        return [4 /*yield*/, sender.account.checkBalance(false)];
                    case 1:
                        _b.sent();
                        _b.label = 2;
                    case 2:
                        if (Amount_1.Amount.equal(drawn, Amount_1.Amount.make(0)))
                            sender.drawn = Amount_1.Amount.make(sender.account.balance.spendable);
                        sender.utxos.length = 0;
                        sender.account.addEventListener(EventDispatcher_1.Event.CHANGE_BALANCE, this.onAccountChangeBalance, this);
                        sender.account.spendableUTXOProvider.clear();
                        return [4 /*yield*/, sender.account.spendableUTXOProvider.getUTXO(sender.drawn)];
                    case 3:
                        res = _b.sent();
                        if (!(res.code === Types_1.WalletResultCode.Success && res.data !== undefined)) return [3 /*break*/, 5];
                        (_a = sender.utxos).push.apply(_a, res.data);
                        sender.calculateUTXOSum();
                        sender.enable = true;
                        return [4 /*yield*/, this.calculate(true)];
                    case 4:
                        _b.sent();
                        return [3 /*break*/, 6];
                    case 5:
                        this.dispatchEvent(EventDispatcher_1.Event.ERROR, res.code);
                        _b.label = 6;
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Remove a sender
     * @param account The account of the sender to be removed
     */
    WalletTxBuilder.prototype.removeSender = function (account) {
        return __awaiter(this, void 0, void 0, function () {
            var sender;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        sender = this._senders.remove(account);
                        if (!(sender !== undefined)) return [3 /*break*/, 2];
                        sender.account.removeEventListener(EventDispatcher_1.Event.CHANGE_BALANCE, this.onAccountChangeBalance, this);
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_SENDER);
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Clear all sender
     */
    WalletTxBuilder.prototype.clearSender = function (is_dispatch) {
        if (is_dispatch === void 0) { is_dispatch = true; }
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this._senders.items.forEach(function (m) {
                            return m.account.removeEventListener(EventDispatcher_1.Event.CHANGE_BALANCE, _this.onAccountChangeBalance, _this);
                        });
                        if (!this._senders.clear()) return [3 /*break*/, 2];
                        if (!is_dispatch) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.calculate(true)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(WalletTxBuilder.prototype, "lengthSender", {
        /**
         * Return the number of the senders
         */
        get: function () {
            return this._senders.length;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Get the sum of receiver's amounts
     */
    WalletTxBuilder.prototype.getTotalReceiverAmount = function () {
        return this._receivers.items.reduce(function (prev, m) { return Amount_1.Amount.add(prev, m.amount); }, Amount_1.Amount.make(0));
    };
    /**
     * Calculate the amount to be withdrawn from all sending accounts.
     * At this time, the number of UTXO to be used increases, and accordingly,
     * the fee increases. Therefore, the withdrawn amount gradually increases as the used UTXO increases.
     */
    WalletTxBuilder.prototype._calculate = function (already_changed) {
        if (already_changed === void 0) { already_changed = false; }
        return __awaiter(this, void 0, void 0, function () {
            var now, out_count, in_count, total_amount, new_fee_payload, new_fee_freezing, changed, new_fee_tx, new_total_drawn, new_remaining, new_total_spendable, done, _i, _a, sender, sender_old_enable, sender_old_spendable, sender_old_drawn, sender_old_remaining, cs_res;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        now = new Date().getTime();
                        if (!this._manual_fee && now - this._latest_fee_rate_time > 60 * 1000) {
                            this._client.getTransactionFee(this._size_tx).then(function (res) {
                                if (res.code === Types_1.WalletResultCode.Success && res.data !== undefined) {
                                    _this._fee_rate = jsbi_1.default.toNumber(Amount_1.Amount.divide(res.data.getFee(_this._fee_option), _this._size_tx).value);
                                    if (_this._fee_rate < Utils_1.Utils.FEE_RATE)
                                        _this._fee_rate = Utils_1.Utils.FEE_RATE;
                                }
                                else {
                                    _this._fee_rate = Utils_1.Utils.FEE_RATE;
                                }
                            });
                            this._latest_fee_rate_time = now;
                        }
                        out_count = this.lengthReceiver;
                        if (out_count === 0)
                            out_count = 2;
                        else
                            out_count++;
                        in_count = 0;
                        total_amount = this.getTotalReceiverAmount();
                        new_fee_payload = TxPayloadFee_1.TxPayloadFee.getFeeAmount(this._payload.length);
                        new_fee_freezing = this._output_type === TxOutput_1.OutputType.Freeze ? Constant_1.Constant.SlashPenaltyAmount : Amount_1.Amount.make(0);
                        changed = false;
                        new_fee_tx = Amount_1.Amount.make(0);
                        new_total_drawn = Amount_1.Amount.make(0);
                        new_remaining = Amount_1.Amount.make(0);
                        new_total_spendable = Amount_1.Amount.make(0);
                        done = false;
                        _i = 0, _a = this._senders.items;
                        _b.label = 1;
                    case 1:
                        if (!(_i < _a.length)) return [3 /*break*/, 6];
                        sender = _a[_i];
                        sender_old_enable = sender.enable;
                        sender_old_spendable = sender.spendable;
                        sender_old_drawn = sender.drawn;
                        sender_old_remaining = sender.remaining;
                        sender.spendable = sender.account.balance.spendable;
                        if (!done) return [3 /*break*/, 2];
                        sender.drawn = Amount_1.Amount.make(0);
                        sender.remaining = Amount_1.Amount.make(0);
                        sender.account.spendableUTXOProvider.giveBack(sender.utxos);
                        sender.utxos.length = 0;
                        sender.total_amount_utxos = Amount_1.Amount.make(0);
                        new_total_spendable = Amount_1.Amount.add(new_total_spendable, sender.spendable);
                        return [3 /*break*/, 4];
                    case 2: return [4 /*yield*/, this.calculateSender(sender, total_amount, new_total_drawn, in_count, out_count, new_fee_payload, new_fee_freezing)];
                    case 3:
                        cs_res = _b.sent();
                        done = cs_res.done;
                        new_fee_tx = cs_res.fee;
                        // It is reflected in the total value.
                        if (sender.enable) {
                            in_count += sender.utxos.length;
                            new_total_drawn = Amount_1.Amount.add(new_total_drawn, sender.drawn);
                            new_remaining = Amount_1.Amount.make(sender.remaining);
                            new_total_spendable = Amount_1.Amount.add(new_total_spendable, sender.spendable);
                        }
                        _b.label = 4;
                    case 4:
                        // If the recipient's value has changed, indicate that it has changed.
                        if (sender_old_enable !== sender.enable ||
                            !Amount_1.Amount.equal(sender_old_spendable, sender.spendable) ||
                            !Amount_1.Amount.equal(sender_old_drawn, sender.drawn) ||
                            !Amount_1.Amount.equal(sender_old_remaining, sender.remaining)) {
                            changed = true;
                        }
                        _b.label = 5;
                    case 5:
                        _i++;
                        return [3 /*break*/, 1];
                    case 6:
                        // If the total value has been changed, indicate it.
                        if (!Amount_1.Amount.equal(this._total_spendable, new_total_spendable) ||
                            !Amount_1.Amount.equal(this._total_drawn, new_total_drawn) ||
                            !Amount_1.Amount.equal(this._remaining, new_remaining)) {
                            changed = true;
                            this._total_spendable = Amount_1.Amount.make(new_total_spendable);
                            this._total_drawn = Amount_1.Amount.make(new_total_drawn);
                            this._remaining = Amount_1.Amount.make(new_remaining);
                        }
                        // If the data has been changed, send an event that it has been changed.
                        if (changed || already_changed) {
                            this.dispatchEvent(EventDispatcher_1.Event.CHANGE_SENDER);
                        }
                        if (!Amount_1.Amount.equal(this._fee_tx, new_fee_tx) || !this._already_change_fee) {
                            this._fee_tx = Amount_1.Amount.make(new_fee_tx);
                            this.dispatchEvent(EventDispatcher_1.Event.CHANGE_TX_FEE, this._fee_tx);
                            this._already_change_fee = true;
                        }
                        if (!Amount_1.Amount.equal(this._fee_payload, new_fee_payload)) {
                            this._fee_payload = Amount_1.Amount.make(new_fee_payload);
                            this.dispatchEvent(EventDispatcher_1.Event.CHANGE_PAYLOAD_FEE, this._fee_payload);
                        }
                        if (!Amount_1.Amount.equal(this._fee_freezing, new_fee_freezing)) {
                            this._fee_freezing = Amount_1.Amount.make(new_fee_freezing);
                            this.dispatchEvent(EventDispatcher_1.Event.CHANGE_FREEZING_FEE, this._fee_freezing);
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Calculate the amount to be withdrawn from all sending accounts.
     * At this time, the number of UTXO to be used increases, and accordingly,
     * the fee increases. Therefore, the withdrawn amount gradually increases as the used UTXO increases.
     * If an error occurs during calculation, initialize the data of all callers and try again.
     */
    WalletTxBuilder.prototype.calculate = function (already_changed) {
        if (already_changed === void 0) { already_changed = false; }
        return __awaiter(this, void 0, void 0, function () {
            var max_try_cnt, success, cnt, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        max_try_cnt = 3;
                        success = false;
                        cnt = 0;
                        _a.label = 1;
                    case 1:
                        if (!(cnt < max_try_cnt && !success)) return [3 /*break*/, 9];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this._calculate(already_changed)];
                    case 3:
                        _a.sent();
                        success = true;
                        return [3 /*break*/, 5];
                    case 4:
                        e_1 = _a.sent();
                        success = false;
                        return [3 /*break*/, 5];
                    case 5:
                        if (!!success) return [3 /*break*/, 8];
                        if (!(cnt < max_try_cnt - 1)) return [3 /*break*/, 7];
                        return [4 /*yield*/, this.initializeSenders()];
                    case 6:
                        _a.sent();
                        return [3 /*break*/, 8];
                    case 7:
                        this.dispatchEvent(EventDispatcher_1.Event.ERROR, Types_1.WalletResultCode.SystemError);
                        _a.label = 8;
                    case 8:
                        cnt++;
                        return [3 /*break*/, 1];
                    case 9: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Calculate the amount to be withdrawn from one sending accounts.
     * @param sender        The sender
     * @param total_amount  The total sending amount
     * @param total_drawn   The sum of the amount to be withdrawn
     * @param in_count      The number of the transaction input
     * @param out_count     The number of the transaction output
     * @param fee_payload   The fee of payload
     * @param fee_freezing  The fee of freezing
     */
    WalletTxBuilder.prototype.calculateSender = function (sender, total_amount, total_drawn, in_count, out_count, fee_payload, fee_freezing) {
        return __awaiter(this, void 0, void 0, function () {
            var done, fee, max_count, sender_done, try_count, sender_remaining, sender_drawn, fee_total, amount, required_amount, fee_per_input, new_fee_tx, new_fee_total, res, new_fee_total, res2;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        done = false;
                        fee = Amount_1.Amount.make(0);
                        max_count = 3;
                        sender_done = false;
                        try_count = 0;
                        _c.label = 1;
                    case 1:
                        if (!(try_count < max_count || !sender_done)) return [3 /*break*/, 11];
                        sender_remaining = Amount_1.Amount.make(0);
                        sender_drawn = Amount_1.Amount.make(0);
                        // Calculate the size of the transaction.
                        this._size_tx = this.getEstimatedSize(in_count + sender.utxos.length, out_count, this._payload.length);
                        // Calculate the fee of the transaction.
                        fee = this.getEstimatedFee(in_count + sender.utxos.length, out_count, this._payload.length);
                        fee_total = Amount_1.Amount.add(Amount_1.Amount.add(fee, fee_payload), fee_freezing);
                        amount = Amount_1.Amount.subtract(Amount_1.Amount.add(total_amount, fee_total), total_drawn);
                        // If this sender can cover all of the balance,
                        if (Amount_1.Amount.greaterThanOrEqual(sender.spendable, amount)) {
                            sender_drawn = Amount_1.Amount.make(amount);
                            sender_remaining = Amount_1.Amount.make(0);
                            done = true;
                        }
                        else {
                            sender_drawn = Amount_1.Amount.make(sender.spendable);
                            sender_remaining = Amount_1.Amount.subtract(amount, sender.spendable);
                            done = false;
                        }
                        if (!(done || Amount_1.Amount.greaterThan(sender_drawn, sender.total_amount_utxos))) return [3 /*break*/, 9];
                        required_amount = void 0;
                        fee_per_input = void 0;
                        if (done) {
                            new_fee_tx = this.getEstimatedFee(in_count, out_count, this._payload.length);
                            new_fee_total = Amount_1.Amount.add(new_fee_tx, fee_payload);
                            required_amount = Amount_1.Amount.subtract(Amount_1.Amount.add(total_amount, new_fee_total), total_drawn);
                            fee_per_input = Amount_1.Amount.make(this._fee_rate * TxInput_1.TxInput.getEstimatedNumberOfBytes());
                        }
                        else {
                            required_amount = sender_drawn;
                            fee_per_input = Amount_1.Amount.make(0);
                        }
                        sender.account.spendableUTXOProvider.giveBack(sender.utxos);
                        sender.utxos.length = 0;
                        sender.total_amount_utxos = Amount_1.Amount.make(0);
                        return [4 /*yield*/, sender.account.spendableUTXOProvider.getUTXO(required_amount, fee_per_input)];
                    case 2:
                        res = _c.sent();
                        if (!(res.code === Types_1.WalletResultCode.Success && res.data !== undefined)) return [3 /*break*/, 7];
                        if (!(res.data.length > 0)) return [3 /*break*/, 3];
                        (_a = sender.utxos).push.apply(_a, res.data);
                        sender.calculateUTXOSum();
                        sender.enable = true;
                        sender.drawn = sender_drawn;
                        sender.remaining = sender_remaining;
                        sender_done = true;
                        return [3 /*break*/, 6];
                    case 3:
                        // This is when the balance is insufficient.
                        done = false;
                        sender.enable = false;
                        new_fee_total = Amount_1.Amount.add(this.getEstimatedFee(in_count + sender.account.spendableUTXOProvider.length, out_count, this._payload.length), fee_payload);
                        required_amount = Amount_1.Amount.subtract(Amount_1.Amount.add(total_amount, new_fee_total), total_drawn);
                        return [4 /*yield*/, sender.account.checkBalance(false)];
                    case 4:
                        _c.sent();
                        sender.spendable = Amount_1.Amount.make(sender.account.balance.spendable);
                        sender.drawn = Amount_1.Amount.make(sender.spendable);
                        if (Amount_1.Amount.greaterThanOrEqual(required_amount, sender.spendable)) {
                            sender.remaining = Amount_1.Amount.subtract(required_amount, sender.spendable);
                        }
                        else {
                            sender.remaining = Amount_1.Amount.make(this._fee_rate * TxInput_1.TxInput.getEstimatedNumberOfBytes() * 10);
                        }
                        sender.account.spendableUTXOProvider.clear();
                        sender.utxos.length = 0;
                        sender.total_amount_utxos = Amount_1.Amount.make(0);
                        return [4 /*yield*/, sender.account.spendableUTXOProvider.getUTXO(sender.spendable)];
                    case 5:
                        res2 = _c.sent();
                        if (res2.code === Types_1.WalletResultCode.Success && res2.data !== undefined && res2.data.length > 0) {
                            (_b = sender.utxos).push.apply(_b, res.data);
                            sender.calculateUTXOSum();
                            new_fee_total = Amount_1.Amount.add(this.getEstimatedFee(in_count + sender.utxos.length, out_count, this._payload.length), fee_payload);
                            required_amount = Amount_1.Amount.subtract(Amount_1.Amount.add(total_amount, new_fee_total), total_drawn);
                            sender.enable = true;
                            sender.drawn = Amount_1.Amount.make(sender.spendable);
                            sender.remaining = Amount_1.Amount.subtract(required_amount, sender.spendable);
                            if (Amount_1.Amount.greaterThanOrEqual(required_amount, sender.spendable)) {
                                sender.remaining = Amount_1.Amount.subtract(required_amount, sender.spendable);
                            }
                            else {
                                sender.remaining = Amount_1.Amount.make(this._fee_rate * TxInput_1.TxInput.getEstimatedNumberOfBytes() * 10);
                            }
                            sender_done = true;
                        }
                        _c.label = 6;
                    case 6: return [3 /*break*/, 8];
                    case 7:
                        done = false;
                        sender.enable = false;
                        sender.spendable = Amount_1.Amount.make(0);
                        sender.drawn = Amount_1.Amount.make(0);
                        sender.remaining = Amount_1.Amount.make(amount);
                        sender.account.spendableUTXOProvider.clear();
                        sender.utxos.length = 0;
                        sender.total_amount_utxos = Amount_1.Amount.make(0);
                        this.dispatchEvent(EventDispatcher_1.Event.ERROR, res.code);
                        _c.label = 8;
                    case 8: return [3 /*break*/, 10];
                    case 9:
                        sender.enable = true;
                        sender.drawn = sender_drawn;
                        sender.remaining = sender_remaining;
                        sender_done = true;
                        _c.label = 10;
                    case 10:
                        try_count++;
                        return [3 /*break*/, 1];
                    case 11: return [2 /*return*/, {
                            done: done,
                            fee: fee,
                        }];
                }
            });
        });
    };
    /**
     * Check the balance of all senders. And initialize the data.
     */
    WalletTxBuilder.prototype.initializeSenders = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _i, _a, sender, res;
            var _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _i = 0, _a = this._senders.items;
                        _c.label = 1;
                    case 1:
                        if (!(_i < _a.length)) return [3 /*break*/, 5];
                        sender = _a[_i];
                        return [4 /*yield*/, sender.account.checkBalance(false)];
                    case 2:
                        _c.sent();
                        sender.drawn = Amount_1.Amount.make(sender.account.balance.spendable);
                        sender.utxos.length = 0;
                        sender.account.spendableUTXOProvider.clear();
                        return [4 /*yield*/, sender.account.spendableUTXOProvider.getUTXO(sender.drawn)];
                    case 3:
                        res = _c.sent();
                        if (res.code === Types_1.WalletResultCode.Success && res.data !== undefined) {
                            (_b = sender.utxos).push.apply(_b, res.data);
                            sender.enable = true;
                        }
                        else {
                            sender.enable = false;
                        }
                        sender.calculateUTXOSum();
                        _c.label = 4;
                    case 4:
                        _i++;
                        return [3 /*break*/, 1];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Get the estimated fee
     * @param num_input         The number of the transaction inputs
     * @param num_output        The number of the transaction outputs
     * @param num_bytes_payload The size of the transaction _payload
     */
    WalletTxBuilder.prototype.getEstimatedFee = function (num_input, num_output, num_bytes_payload) {
        return Amount_1.Amount.make(this._fee_rate * this.getEstimatedSize(num_input, num_output, num_bytes_payload));
    };
    /**
     * Get the estimated transaction size
     * @param num_input         The number of the transaction inputs
     * @param num_output        The number of the transaction outputs
     * @param num_bytes_payload The size of the transaction _payload
     */
    WalletTxBuilder.prototype.getEstimatedSize = function (num_input, num_output, num_bytes_payload) {
        return Transaction_1.Transaction.getEstimatedNumberOfBytes(Math.max(num_input, 1), Math.max(num_output, 2), num_bytes_payload);
    };
    /**
     * Event Handler on change balance of an account
     * @param type The event type
     * @param account The instance of an account
     */
    WalletTxBuilder.prototype.onAccountChangeBalance = function (type, account) {
        return __awaiter(this, void 0, void 0, function () {
            var sender, res;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        sender = this._senders.items.find(function (m) { return m.account === account; });
                        if (sender === undefined)
                            return [2 /*return*/];
                        sender.drawn = Amount_1.Amount.make(sender.spendable);
                        sender.spendable = Amount_1.Amount.make(sender.account.balance.spendable);
                        sender.total_amount_utxos = Amount_1.Amount.make(0);
                        sender.utxos.length = 0;
                        sender.account.spendableUTXOProvider.clear();
                        return [4 /*yield*/, sender.account.spendableUTXOProvider.getUTXO(sender.drawn)];
                    case 1:
                        res = _b.sent();
                        if (!(res.code === Types_1.WalletResultCode.Success && res.data !== undefined)) return [3 /*break*/, 3];
                        (_a = sender.utxos).push.apply(_a, res.data);
                        sender.calculateUTXOSum();
                        sender.enable = true;
                        return [4 /*yield*/, this.calculate(true)];
                    case 2:
                        _b.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        sender.enable = false;
                        this.dispatchEvent(EventDispatcher_1.Event.ERROR, res.code);
                        _b.label = 4;
                    case 4: return [4 /*yield*/, this.calculate()];
                    case 5:
                        _b.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Set the payload
     * @param payload The data to be stored in the transaction
     */
    WalletTxBuilder.prototype.setPayload = function (payload) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this._payload = payload;
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Set the option of transaction fee
     * @param value The option value  (High, Medium, Low)
     */
    WalletTxBuilder.prototype.setFeeOption = function (value) {
        return __awaiter(this, void 0, void 0, function () {
            var fee_res;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this._manual_fee = false;
                        return [4 /*yield*/, this._client.getTransactionFee(this._size_tx)];
                    case 1:
                        fee_res = _a.sent();
                        if (fee_res.code === Types_1.WalletResultCode.Success && fee_res.data !== undefined) {
                            this._fee_option = value;
                            this._fee_rate = jsbi_1.default.toNumber(Amount_1.Amount.divide(fee_res.data.getFee(this._fee_option), this._size_tx).value);
                            if (this._fee_rate < Utils_1.Utils.FEE_RATE)
                                this._fee_rate = Utils_1.Utils.FEE_RATE;
                        }
                        else {
                            this._fee_rate = Utils_1.Utils.FEE_RATE;
                        }
                        return [4 /*yield*/, this.calculate()];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Set the transaction fee, The adjusted value is returned.
     * After calculating internally, an event occurs about the change in fees.
     * @param tx_fee The fee of transaction
     */
    WalletTxBuilder.prototype.setTransactionFee = function (tx_fee) {
        return __awaiter(this, void 0, void 0, function () {
            var num_input, _i, _a, sender, _fee_rate;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        this._manual_fee = true;
                        num_input = 0;
                        for (_i = 0, _a = this._senders.items; _i < _a.length; _i++) {
                            sender = _a[_i];
                            num_input += sender.utxos.length;
                        }
                        _fee_rate = jsbi_1.default.toNumber(Amount_1.Amount.divide(tx_fee, this._size_tx).value);
                        if (_fee_rate < Utils_1.Utils.FEE_RATE) {
                            this._fee_rate = Utils_1.Utils.FEE_RATE;
                        }
                        else {
                            this._fee_rate = _fee_rate;
                        }
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _b.sent();
                        return [2 /*return*/, Amount_1.Amount.multiply(Amount_1.Amount.make(this._fee_rate), this._size_tx)];
                }
            });
        });
    };
    /**
     * Clear all data
     * @param is_dispatch
     */
    WalletTxBuilder.prototype.clear = function (is_dispatch) {
        if (is_dispatch === void 0) { is_dispatch = true; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.clearReceiver(is_dispatch)];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.clearSender(is_dispatch)];
                    case 2:
                        _a.sent();
                        this._fee_rate = Utils_1.Utils.FEE_RATE;
                        this._fee_tx = this.getEstimatedFee(1, 2, 0);
                        this._fee_payload = Amount_1.Amount.make(0);
                        this._total_drawn = Amount_1.Amount.make(0);
                        this._remaining = Amount_1.Amount.make(0);
                        this._total_spendable = Amount_1.Amount.make(0);
                        this._payload = Buffer.alloc(0);
                        this._size_tx = Transaction_1.Transaction.getEstimatedNumberOfBytes(1, 2, 0);
                        this._latest_fee_rate_time = 0;
                        this._manual_fee = false;
                        this._already_change_fee = false;
                        return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(WalletTxBuilder.prototype, "fee_rate", {
        /**
         * The ratio of fees to the size of the transaction
         */
        get: function () {
            return this._fee_rate;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletTxBuilder.prototype, "fee_tx", {
        /**
         * The fee of the transaction
         */
        get: function () {
            return this._fee_tx;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletTxBuilder.prototype, "fee_payload", {
        /**
         * The fee of _payload
         */
        get: function () {
            return this._fee_payload;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletTxBuilder.prototype, "total_drawn", {
        /**
         * The amount to be spent
         */
        get: function () {
            return this._total_drawn;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletTxBuilder.prototype, "remaining", {
        /**
         * The amount not enough.
         */
        get: function () {
            return this._remaining;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletTxBuilder.prototype, "total_spendable", {
        /**
         * The amount that can be used in the account.
         */
        get: function () {
            return this._total_spendable;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletTxBuilder.prototype, "payload", {
        /**
         * The payload of a transaction
         */
        get: function () {
            return this._payload;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletTxBuilder.prototype, "fee_option", {
        /**
         * The option of fee
         */
        get: function () {
            return this._fee_option;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletTxBuilder.prototype, "receivers", {
        /**
         * The storage of the receivers
         */
        get: function () {
            return this._receivers;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletTxBuilder.prototype, "senders", {
        /**
         * The storage of the senders
         */
        get: function () {
            return this._senders;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Check if there is a condition to create a transaction.
     */
    WalletTxBuilder.prototype.validate = function () {
        if (this._payload.length === 0) {
            if (this.lengthReceiver < 1)
                return {
                    code: Types_1.WalletResultCode.NotAssignedReceiver,
                    message: Types_1.WalletMessage.NotAssignedReceiver,
                };
            var totalAmount = this.getTotalReceiverAmount();
            if (Amount_1.Amount.lessThanOrEqual(totalAmount, Amount_1.Amount.make(0)))
                return {
                    code: Types_1.WalletResultCode.NotAssignedReceiverAmount,
                    message: Types_1.WalletMessage.NotAssignedReceiverAmount,
                };
        }
        if (this.receivers.items.some(function (m) { return Amount_1.Amount.equal(m.amount, Amount_1.Amount.ZERO_BOA); }))
            return {
                code: Types_1.WalletResultCode.AmountIsZero,
                message: Types_1.WalletMessage.AmountIsZero,
            };
        if (Amount_1.Amount.greaterThan(this._remaining, Amount_1.Amount.make(0)))
            return {
                code: Types_1.WalletResultCode.InsufficientAmount,
                message: Types_1.WalletMessage.InsufficientAmount,
            };
        return {
            code: Types_1.WalletResultCode.Success,
            message: Types_1.WalletMessage.Success,
        };
    };
    /**
     * Build a transaction.
     */
    WalletTxBuilder.prototype.buildTransaction = function () {
        var res_valid = this.validate();
        if (res_valid.code !== Types_1.WalletResultCode.Success)
            return res_valid;
        if (this.getReadOnlyAccount().length > 0) {
            return { code: Types_1.WalletResultCode.ExistUnknownSecretKey, message: Types_1.WalletMessage.ExistUnknownSecretKey };
        }
        var refund_account = this._senders.items
            .filter(function (m) { return m.enable; })
            .find(function (m, idx, obj) {
            return Amount_1.Amount.equal(m.remaining, Amount_1.Amount.ZERO_BOA) || idx === obj.length - 1;
        });
        if (refund_account === undefined) {
            return { code: Types_1.WalletResultCode.NotAssignedSender, message: Types_1.WalletMessage.NotAssignedSender };
        }
        var refund_keypair;
        if (refund_account.account.secret !== undefined) {
            refund_keypair = KeyPair_1.KeyPair.fromSeed(refund_account.account.secret);
        }
        else {
            return { code: Types_1.WalletResultCode.ExistUnknownSecretKey, message: Types_1.WalletMessage.ExistUnknownSecretKey };
        }
        var tx;
        try {
            var builder_1 = new TxBuilder_1.TxBuilder(refund_keypair);
            if (this._payload.length > 0)
                builder_1.assignPayload(this._payload);
            this._senders.items.forEach(function (s) {
                s.utxos.forEach(function (m) {
                    builder_1.addInput(m.type, m.utxo, m.amount, s.account.secret);
                });
            });
            this._receivers.items.forEach(function (r) {
                builder_1.addOutput(r.address, r.amount);
            });
            tx = builder_1.sign(this._output_type, this._fee_tx, this._fee_payload, this._fee_freezing);
        }
        catch (e) {
            return { code: Types_1.WalletResultCode.FailedBuildTransaction, message: Types_1.WalletMessage.FailedBuildTransaction };
        }
        return {
            code: Types_1.WalletResultCode.Success,
            message: Types_1.WalletMessage.Success,
            data: tx,
        };
    };
    /**
     * Get the overview of the transaction built
     */
    WalletTxBuilder.prototype.getTransactionOverview = function () {
        var res = this.buildTransaction();
        if (res.code !== Types_1.WalletResultCode.Success || res.data === undefined)
            return {
                code: res.code,
                message: res.message,
            };
        var tx = res.data;
        var tx_hash = Hash_1.hashFull(tx);
        var r = [];
        for (var idx = 0; idx < tx.outputs.length; idx++) {
            r.push({
                utxo: Hash_1.makeUTXOKey(tx_hash, jsbi_1.default.BigInt(idx)),
                address: tx.outputs[idx].address,
                amount: tx.outputs[idx].value,
            });
        }
        var s = [];
        var _loop_1 = function (input) {
            var found = void 0;
            for (var _i = 0, _a = this_1._senders.items; _i < _a.length; _i++) {
                var sender = _a[_i];
                found = sender.utxos.find(function (utxo) { return Hash_1.Hash.equal(utxo.utxo, input.utxo); });
                if (found !== undefined) {
                    s.push({
                        utxo: found.utxo,
                        address: sender.account.address,
                        amount: found.amount,
                    });
                    break;
                }
            }
        };
        var this_1 = this;
        for (var _i = 0, _a = tx.inputs; _i < _a.length; _i++) {
            var input = _a[_i];
            _loop_1(input);
        }
        return {
            code: Types_1.WalletResultCode.Success,
            message: Types_1.WalletMessage.Success,
            data: {
                receivers: r,
                senders: s,
                payload: Buffer.from(this._payload),
                fee_tx: Amount_1.Amount.make(this._fee_tx),
                fee_payload: Amount_1.Amount.make(this._fee_payload),
                fee_freezing: Amount_1.Amount.make(this._fee_freezing),
            },
        };
    };
    /**
     * Returns accounts that require a secret key input.
     */
    WalletTxBuilder.prototype.getReadOnlyAccount = function () {
        return this._senders.readonly_accounts;
    };
    return WalletTxBuilder;
}(EventDispatcher_1.EventDispatcher));
exports.WalletTxBuilder = WalletTxBuilder;
/**
 * The transaction builder for multi sender, single receiver
 */
var WalletTxBuilderSingleReceiver = /** @class */ (function (_super) {
    __extends(WalletTxBuilderSingleReceiver, _super);
    function WalletTxBuilderSingleReceiver() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Set the address of receiver
     */
    WalletTxBuilderSingleReceiver.prototype.setReceiverAddress = function (address) {
        return __awaiter(this, void 0, void 0, function () {
            var changed;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        changed = false;
                        if (this._receiver_address === undefined || !KeyPair_1.PublicKey.equal(this._receiver_address, address)) {
                            changed = true;
                        }
                        this._receiver_address = new KeyPair_1.PublicKey(address.data);
                        if (!(this._receiver_amount !== undefined)) return [3 /*break*/, 2];
                        this._receivers.clear();
                        this._receivers.add({
                            address: this._receiver_address,
                            amount: this._receiver_amount,
                        });
                        if (!changed) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        if (changed)
                            this.dispatchEvent(EventDispatcher_1.Event.CHANGE_RECEIVER);
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Set the amount of receiver
     */
    WalletTxBuilderSingleReceiver.prototype.setReceiverAmount = function (amount) {
        return __awaiter(this, void 0, void 0, function () {
            var changed;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        changed = false;
                        if (this._receiver_amount === undefined || Amount_1.Amount.notEqual(this._receiver_amount, amount)) {
                            changed = true;
                        }
                        this._receiver_amount = Amount_1.Amount.make(amount);
                        if (!(this._receiver_address !== undefined)) return [3 /*break*/, 2];
                        this._receivers.clear();
                        this._receivers.add({
                            address: this._receiver_address,
                            amount: this._receiver_amount,
                        });
                        if (!changed) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        if (changed)
                            this.dispatchEvent(EventDispatcher_1.Event.CHANGE_RECEIVER);
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Add a receiver
     * @param receiver  the receiver to be added
     * @param replace   Specify whether to change the amount or ignore it when there is one with the same address.
     * If this value is true, if the same account address exists, it will be changed to a new amount.
     */
    WalletTxBuilderSingleReceiver.prototype.addReceiver = function (receiver, replace) {
        if (replace === void 0) { replace = true; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this._receivers.length > 0)
                            this._receivers.clear();
                        if (!this._receivers.add(receiver, replace)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_RECEIVER);
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Remove all receivers
     */
    WalletTxBuilderSingleReceiver.prototype.clearReceiver = function (is_dispatch) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this._receivers.clear()) return [3 /*break*/, 2];
                        if (!is_dispatch) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_RECEIVER);
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(WalletTxBuilderSingleReceiver.prototype, "receiver_address", {
        get: function () {
            return this._receiver_address;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletTxBuilderSingleReceiver.prototype, "receiver_amount", {
        get: function () {
            return this._receiver_amount;
        },
        enumerable: false,
        configurable: true
    });
    return WalletTxBuilderSingleReceiver;
}(WalletTxBuilder));
exports.WalletTxBuilderSingleReceiver = WalletTxBuilderSingleReceiver;
var AccountUTXOSummary = /** @class */ (function () {
    function AccountUTXOSummary() {
        this.items = [];
    }
    AccountUTXOSummary.prototype.aggregate = function (account, utxo) {
        var found = this.items.find(function (m) { return KeyPair_1.PublicKey.equal(m.account.address, account.address); });
        if (found !== undefined) {
            found.to_be_unfrozen = Amount_1.Amount.add(found.to_be_unfrozen, utxo.amount);
        }
        else {
            this.items.push({
                account: account,
                to_be_unfrozen: Amount_1.Amount.make(utxo.amount),
                deposit: Amount_1.Amount.make(Constant_1.Constant.SlashPenaltyAmount),
                frozen_balance: Amount_1.Amount.make(account.balance.frozen),
                total_balance: Amount_1.Amount.make(account.balance.balance),
            });
        }
    };
    AccountUTXOSummary.prototype.clear = function () {
        this.items.length = 0;
    };
    return AccountUTXOSummary;
}());
exports.AccountUTXOSummary = AccountUTXOSummary;
/**
 * Create a transaction for unfreeze.
 * This provides a function to selectively unfreezing of the added account among frozen UTXOs.
 * Among the members, `WalletUnfreezeBuilder.utxo` provides all UTXO of the added account.
 * `WalletUnfreezeBuilder.summary` is information of selected UTXOs and the balance of the account.
 */
var WalletUnfreezeBuilder = /** @class */ (function (_super) {
    __extends(WalletUnfreezeBuilder, _super);
    /**
     * Constructor
     * @param client The wallet client to request
     */
    function WalletUnfreezeBuilder(client) {
        var _this = _super.call(this, client) || this;
        _this._unfreeze_amount = Amount_1.Amount.make(0);
        _this._utxos = [];
        _this._summary = new AccountUTXOSummary();
        return _this;
    }
    /**
     * Add a sender
     * @param account The account of the sender to be added
     */
    WalletUnfreezeBuilder.prototype.addSender = function (account) {
        return __awaiter(this, void 0, void 0, function () {
            var found, sender_1, res;
            var _a, _b;
            var _this = this;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        found = this._senders.items.find(function (value) { return KeyPair_1.PublicKey.equal(value.account.address, account.address); });
                        if (!(found === undefined)) return [3 /*break*/, 6];
                        this._senders.items.forEach(function (m) {
                            return m.account.removeEventListener(EventDispatcher_1.Event.CHANGE_BALANCE, _this.onAccountChangeBalance, _this);
                        });
                        this._senders.clear();
                        sender_1 = this._senders.add(account, Amount_1.Amount.make(0));
                        if (!(sender_1 !== undefined)) return [3 /*break*/, 6];
                        if (!!sender_1.account.balance.enable) return [3 /*break*/, 2];
                        return [4 /*yield*/, sender_1.account.checkBalance(false)];
                    case 1:
                        _c.sent();
                        _c.label = 2;
                    case 2:
                        sender_1.drawn = Amount_1.Amount.make(sender_1.account.balance.frozen);
                        sender_1.utxos.length = 0;
                        sender_1.account.addEventListener(EventDispatcher_1.Event.CHANGE_BALANCE, this.onAccountChangeBalance, this);
                        sender_1.account.frozenUTXOProvider.clear();
                        return [4 /*yield*/, sender_1.account.frozenUTXOProvider.getUTXO(sender_1.drawn)];
                    case 3:
                        res = _c.sent();
                        if (!(res.code === Types_1.WalletResultCode.Success && res.data !== undefined)) return [3 /*break*/, 5];
                        (_a = sender_1.utxos).push.apply(_a, res.data);
                        sender_1.calculateUTXOSum();
                        sender_1.enable = true;
                        (_b = this._utxos).push.apply(_b, sender_1.utxos.map(function (u) {
                            return { account: sender_1.account, utxo: u, selected: false };
                        }));
                        return [4 /*yield*/, this.calculate()];
                    case 4:
                        _c.sent();
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_UTXO_ITEM);
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_SENDER);
                        return [3 /*break*/, 6];
                    case 5:
                        this.dispatchEvent(EventDispatcher_1.Event.ERROR, res.code);
                        _c.label = 6;
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Remove a sender
     * @param account The account of the sender to be removed
     */
    WalletUnfreezeBuilder.prototype.removeSender = function (account) {
        return __awaiter(this, void 0, void 0, function () {
            var sender, filtered;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        sender = this._senders.remove(account);
                        if (!(sender !== undefined)) return [3 /*break*/, 2];
                        sender.account.removeEventListener(EventDispatcher_1.Event.CHANGE_BALANCE, this.onAccountChangeBalance, this);
                        filtered = this._utxos.filter(function (m) { return !KeyPair_1.PublicKey.equal(m.account.address, sender.account.address); });
                        this._utxos.length = 0;
                        (_a = this._utxos).push.apply(_a, filtered);
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _b.sent();
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_UTXO_ITEM);
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_SENDER);
                        _b.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Clear all sender
     */
    WalletUnfreezeBuilder.prototype.clearSender = function (is_dispatch) {
        if (is_dispatch === void 0) { is_dispatch = true; }
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this._senders.items.forEach(function (m) {
                            return m.account.removeEventListener(EventDispatcher_1.Event.CHANGE_BALANCE, _this.onAccountChangeBalance, _this);
                        });
                        if (!this._senders.clear()) return [3 /*break*/, 3];
                        this._utxos.length = 0;
                        if (!is_dispatch) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_UTXO_ITEM);
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_SENDER);
                        _a.label = 3;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    WalletUnfreezeBuilder.prototype.getSelectedUTXO = function () {
        return this._utxos.filter(function (m) { return m.selected; });
    };
    WalletUnfreezeBuilder.prototype.makeSummary = function () {
        var _this = this;
        this._summary.clear();
        this.getSelectedUTXO().forEach(function (m) { return _this._summary.aggregate(m.account, m.utxo); });
    };
    WalletUnfreezeBuilder.prototype.selectUTXO = function (utxo, value) {
        return __awaiter(this, void 0, void 0, function () {
            var found;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        found = this._utxos.find(function (m) { return Hash_1.Hash.equal(m.utxo.utxo, utxo); });
                        if (!(found !== undefined)) return [3 /*break*/, 2];
                        if (!(found.selected !== value)) return [3 /*break*/, 2];
                        found.selected = value;
                        this.makeSummary();
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_UTXO_SELECTION, found);
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_UTXO_SUMMARY);
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Get the estimated fee
     * @param num_input         The number of the transaction inputs
     * @param num_output        The number of the transaction outputs
     * @param num_bytes_payload The size of the transaction _payload
     */
    WalletUnfreezeBuilder.prototype.getEstimatedFee = function (num_input, num_output, num_bytes_payload) {
        return Amount_1.Amount.make(this._fee_rate * this.getEstimatedSize(num_input, num_output, num_bytes_payload));
    };
    /**
     * Get the estimated transaction size
     * @param num_input         The number of the transaction inputs
     * @param num_output        The number of the transaction outputs
     * @param num_bytes_payload The size of the transaction _payload
     */
    WalletUnfreezeBuilder.prototype.getEstimatedSize = function (num_input, num_output, num_bytes_payload) {
        return Transaction_1.Transaction.getEstimatedNumberOfBytes(Math.max(num_input, 1), Math.max(num_output, 1), num_bytes_payload);
    };
    /**
     * Event Handler on change balance of an account
     * @param type The event type
     * @param account The instance of an account
     */
    WalletUnfreezeBuilder.prototype.onAccountChangeBalance = function (type, account) {
        return __awaiter(this, void 0, void 0, function () {
            var sender, res;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        sender = this._senders.items.find(function (m) { return m.account === account; });
                        if (sender === undefined)
                            return [2 /*return*/];
                        sender.drawn = Amount_1.Amount.make(sender.account.balance.frozen);
                        sender.spendable = Amount_1.Amount.make(sender.account.balance.spendable);
                        sender.total_amount_utxos = Amount_1.Amount.make(0);
                        sender.utxos.length = 0;
                        sender.account.frozenUTXOProvider.clear();
                        return [4 /*yield*/, sender.account.frozenUTXOProvider.getUTXO(sender.drawn)];
                    case 1:
                        res = _b.sent();
                        if (!(res.code === Types_1.WalletResultCode.Success && res.data !== undefined)) return [3 /*break*/, 3];
                        (_a = sender.utxos).push.apply(_a, res.data);
                        sender.calculateUTXOSum();
                        sender.enable = true;
                        return [4 /*yield*/, this.calculate()];
                    case 2:
                        _b.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        sender.enable = false;
                        this.dispatchEvent(EventDispatcher_1.Event.ERROR, res.code);
                        _b.label = 4;
                    case 4: return [4 /*yield*/, this.calculate()];
                    case 5:
                        _b.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Set the option of transaction fee
     * @param value The option value  (High, Medium, Low)
     */
    WalletUnfreezeBuilder.prototype.setFeeOption = function (value) {
        return __awaiter(this, void 0, void 0, function () {
            var selected, tx_size, fee_res;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        selected = this.getSelectedUTXO();
                        tx_size = this.getEstimatedSize(selected.length, 1, 0);
                        return [4 /*yield*/, this._client.getTransactionFee(tx_size)];
                    case 1:
                        fee_res = _a.sent();
                        if (fee_res.code === Types_1.WalletResultCode.Success && fee_res.data !== undefined) {
                            this._fee_option = value;
                            this._fee_rate = jsbi_1.default.toNumber(Amount_1.Amount.divide(fee_res.data.getFee(this._fee_option), tx_size).value);
                            if (this._fee_rate < Utils_1.Utils.FEE_RATE)
                                this._fee_rate = Utils_1.Utils.FEE_RATE;
                        }
                        else {
                            this._fee_rate = Utils_1.Utils.FEE_RATE;
                        }
                        return [4 /*yield*/, this.calculate()];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Set the transaction fee, The adjusted value is returned.
     * After calculating internally, an event occurs about the change in fees.
     * @param tx_fee The fee of transaction
     */
    WalletUnfreezeBuilder.prototype.setTransactionFee = function (tx_fee) {
        return __awaiter(this, void 0, void 0, function () {
            var selected, tx_size, _fee_rate;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        selected = this.getSelectedUTXO();
                        tx_size = this.getEstimatedSize(selected.length, 1, 0);
                        _fee_rate = jsbi_1.default.toNumber(Amount_1.Amount.divide(tx_fee, tx_size).value);
                        if (_fee_rate < Utils_1.Utils.FEE_RATE) {
                            this._fee_rate = Utils_1.Utils.FEE_RATE;
                        }
                        else {
                            this._fee_rate = _fee_rate;
                        }
                        return [4 /*yield*/, this.calculate()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, Amount_1.Amount.multiply(Amount_1.Amount.make(this._fee_rate), tx_size)];
                }
            });
        });
    };
    Object.defineProperty(WalletUnfreezeBuilder.prototype, "fee_payload", {
        /**
         * The fee of _payload
         */
        get: function () {
            return Amount_1.Amount.make(0);
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Set the payload
     * @deprecated
     */
    WalletUnfreezeBuilder.prototype.setPayload = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/];
            });
        });
    };
    Object.defineProperty(WalletUnfreezeBuilder.prototype, "payload", {
        /**
         * The payload of a transaction
         */
        get: function () {
            return Buffer.alloc(0);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletUnfreezeBuilder.prototype, "utxos", {
        /**
         * Account and frozen UTXO's list.
         */
        get: function () {
            return this._utxos;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletUnfreezeBuilder.prototype, "summary", {
        /**
         * Summary to be unfrozen by account.
         */
        get: function () {
            return this._summary;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletUnfreezeBuilder.prototype, "unfreeze_amount", {
        /**
         * The amount to be unfrozen
         */
        get: function () {
            return Amount_1.Amount.make(this._unfreeze_amount);
        },
        enumerable: false,
        configurable: true
    });
    /**
     * The amount of UTXO for freeze release is aggregated and fees are calculated.
     */
    WalletUnfreezeBuilder.prototype.calculate = function () {
        return __awaiter(this, void 0, void 0, function () {
            var selected, in_count, out_count, sumOfUTXO, tx_size, new_fee_tx, new_unfreeze_amount;
            return __generator(this, function (_a) {
                selected = this.getSelectedUTXO();
                in_count = selected.length;
                out_count = 1;
                sumOfUTXO = selected.reduce(function (prev, u) { return Amount_1.Amount.add(Amount_1.Amount.add(prev, u.utxo.amount), Constant_1.Constant.SlashPenaltyAmount); }, Amount_1.Amount.make(0));
                tx_size = this.getEstimatedSize(in_count, out_count, 0);
                new_fee_tx = Amount_1.Amount.make(this._fee_rate * tx_size);
                if (Amount_1.Amount.greaterThanOrEqual(sumOfUTXO, new_fee_tx)) {
                    new_unfreeze_amount = Amount_1.Amount.subtract(sumOfUTXO, new_fee_tx);
                    if (!Amount_1.Amount.equal(this._fee_tx, new_fee_tx)) {
                        this._fee_tx = Amount_1.Amount.make(new_fee_tx);
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_TX_FEE, this._fee_tx);
                    }
                    if (!Amount_1.Amount.equal(this._unfreeze_amount, new_unfreeze_amount)) {
                        this._unfreeze_amount = Amount_1.Amount.make(new_unfreeze_amount);
                        this.dispatchEvent(EventDispatcher_1.Event.CHANGE_RECEIVER);
                    }
                }
                return [2 /*return*/];
            });
        });
    };
    /**
     * Clear all data
     * @param is_dispatch
     */
    WalletUnfreezeBuilder.prototype.clear = function (is_dispatch) {
        if (is_dispatch === void 0) { is_dispatch = true; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.clearReceiver(is_dispatch)];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.clearSender(is_dispatch)];
                    case 2:
                        _a.sent();
                        this._fee_rate = Utils_1.Utils.FEE_RATE;
                        this._fee_tx = this.getEstimatedFee(1, 1, 0);
                        this._fee_payload = Amount_1.Amount.make(0);
                        this._total_drawn = Amount_1.Amount.make(0);
                        this._remaining = Amount_1.Amount.make(0);
                        this._payload = Buffer.alloc(0);
                        this._unfreeze_amount = Amount_1.Amount.make(0);
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Check if there is a condition to create a transaction.
     */
    WalletUnfreezeBuilder.prototype.validate = function () {
        var selected = this.getSelectedUTXO();
        if (selected.length === 0)
            return {
                code: Types_1.WalletResultCode.Unfreeze_NotAssignedUTXO,
                message: Types_1.WalletMessage.Unfreeze_NotAssignedUTXO,
            };
        return {
            code: Types_1.WalletResultCode.Success,
            message: Types_1.WalletMessage.Success,
        };
    };
    /**
     * Build a transaction.
     */
    WalletUnfreezeBuilder.prototype.buildTransaction = function () {
        var res_valid = this.validate();
        if (res_valid.code !== Types_1.WalletResultCode.Success)
            return res_valid;
        if (this.getReadOnlyAccount().length > 0) {
            return { code: Types_1.WalletResultCode.ExistUnknownSecretKey, message: Types_1.WalletMessage.ExistUnknownSecretKey };
        }
        if (this.lengthSender === 0) {
            return { code: Types_1.WalletResultCode.NotAssignedSender, message: Types_1.WalletMessage.NotAssignedSender };
        }
        var account = this._senders.items[0].account;
        var keypair;
        if (account.secret !== undefined) {
            keypair = KeyPair_1.KeyPair.fromSeed(account.secret);
        }
        else {
            return { code: Types_1.WalletResultCode.ExistUnknownSecretKey, message: Types_1.WalletMessage.ExistUnknownSecretKey };
        }
        var tx;
        try {
            var selected = this.getSelectedUTXO();
            var builder_2 = new TxBuilder_1.TxBuilder(keypair);
            selected.forEach(function (m) {
                builder_2.addInput(TxOutput_1.OutputType.Freeze, m.utxo.utxo, m.utxo.amount, m.account.secret);
            });
            builder_2.addOutput(keypair.address, this._unfreeze_amount);
            tx = builder_2.sign(TxOutput_1.OutputType.Payment, this._fee_tx, Amount_1.Amount.make(0));
        }
        catch (e) {
            return { code: Types_1.WalletResultCode.FailedBuildTransaction, message: Types_1.WalletMessage.FailedBuildTransaction };
        }
        return {
            code: Types_1.WalletResultCode.Success,
            message: Types_1.WalletMessage.Success,
            data: tx,
        };
    };
    /**
     * Get the overview of the transaction built
     */
    WalletUnfreezeBuilder.prototype.getTransactionOverview = function () {
        var res = this.buildTransaction();
        if (res.code !== Types_1.WalletResultCode.Success || res.data === undefined)
            return {
                code: res.code,
                message: res.message,
            };
        var tx = res.data;
        var tx_hash = Hash_1.hashFull(tx);
        var r = [];
        for (var idx = 0; idx < tx.outputs.length; idx++) {
            r.push({
                utxo: Hash_1.makeUTXOKey(tx_hash, jsbi_1.default.BigInt(idx)),
                address: tx.outputs[idx].address,
                amount: tx.outputs[idx].value,
            });
        }
        var s = [];
        var _loop_2 = function (input) {
            var found = this_2._utxos.find(function (m) { return Hash_1.Hash.equal(m.utxo.utxo, input.utxo); });
            if (found !== undefined) {
                s.push({
                    utxo: found.utxo.utxo,
                    address: found.account.address,
                    amount: found.utxo.amount,
                });
            }
        };
        var this_2 = this;
        for (var _i = 0, _a = tx.inputs; _i < _a.length; _i++) {
            var input = _a[_i];
            _loop_2(input);
        }
        return {
            code: Types_1.WalletResultCode.Success,
            message: Types_1.WalletMessage.Success,
            data: {
                receivers: r,
                senders: s,
                payload: this.payload,
                fee_tx: this.fee_tx,
                fee_payload: this.fee_payload,
                fee_freezing: Amount_1.Amount.make(0),
            },
        };
    };
    return WalletUnfreezeBuilder;
}(WalletTxBuilder));
exports.WalletUnfreezeBuilder = WalletUnfreezeBuilder;
/**
 * Create a transaction to cancel the pending transaction.
 */
var WalletCancelBuilder = /** @class */ (function (_super) {
    __extends(WalletCancelBuilder, _super);
    /**
     * Constructor
     * @param client The wallet client to request
     * @param accounts The accounts
     */
    function WalletCancelBuilder(client, accounts) {
        var _this = _super.call(this, client) || this;
        _this._utxos = [];
        _this._accounts = accounts;
        return _this;
    }
    /**
     * Set up a pending transaction
     * @param tx The pending transaction
     */
    WalletCancelBuilder.prototype.setTransaction = function (tx) {
        return __awaiter(this, void 0, void 0, function () {
            var res;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this._tx = tx;
                        return [4 /*yield*/, this.makeUTXO()];
                    case 1:
                        res = _a.sent();
                        if (res.code !== Types_1.WalletResultCode.Success) {
                            return [2 /*return*/, { code: res.code, message: res.message }];
                        }
                        return [4 /*yield*/, this.makeSender()];
                    case 2:
                        _a.sent();
                        return [2 /*return*/, { code: Types_1.WalletResultCode.Success, message: Types_1.WalletMessage.Success }];
                }
            });
        });
    };
    /**
     * Set the pending transaction hash
     * @param tx_hash The pending transaction hash
     */
    WalletCancelBuilder.prototype.setTransactionHash = function (tx_hash) {
        return __awaiter(this, void 0, void 0, function () {
            var res, e_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this._client.getPendingTransaction(tx_hash)];
                    case 1:
                        res = _a.sent();
                        if (res.code !== Types_1.WalletResultCode.Success || res.data === undefined) {
                            return [2 /*return*/, { code: res.code, message: res.message }];
                        }
                        if (res.data.isCoinbase()) {
                            return [2 /*return*/, {
                                    code: Types_1.WalletResultCode.Cancel_NotAllowCoinbase,
                                    message: Types_1.WalletMessage.Cancel_NotAllowCoinbase,
                                }];
                        }
                        return [2 /*return*/, this.setTransaction(res.data)];
                    case 2:
                        e_2 = _a.sent();
                        return [2 /*return*/, {
                                code: Types_1.WalletResultCode.FailedRequestPendingTransaction,
                                message: Types_1.WalletMessage.FailedRequestPendingTransaction,
                            }];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Find UTXO information used in a set transaction.
     */
    WalletCancelBuilder.prototype.makeUTXO = function () {
        return __awaiter(this, void 0, void 0, function () {
            var utxo_res, e_3;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (this._tx === undefined) {
                            return [2 /*return*/, {
                                    code: Types_1.WalletResultCode.Cancel_NotAssignedTx,
                                    message: Types_1.WalletMessage.Cancel_NotAssignedTx,
                                }];
                        }
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._client.getUTXOInfo(this._tx.inputs.map(function (m) { return m.utxo; }))];
                    case 2:
                        utxo_res = _b.sent();
                        if (utxo_res.code !== Types_1.WalletResultCode.Success || utxo_res.data === undefined) {
                            return [2 /*return*/, { code: Types_1.WalletResultCode.FailedRequestUTXO, message: Types_1.WalletMessage.FailedRequestUTXO }];
                        }
                        this._utxos.length = 0;
                        (_a = this._utxos).push.apply(_a, utxo_res.data);
                        return [2 /*return*/, { code: Types_1.WalletResultCode.Success, message: Types_1.WalletMessage.Success }];
                    case 3:
                        e_3 = _b.sent();
                        return [2 /*return*/, { code: Types_1.WalletResultCode.FailedRequestUTXO, message: Types_1.WalletMessage.FailedRequestUTXO }];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Extract the account needed for the cancellation transaction from the account container
     * and register it as a Sender.
     * If it cannot be found in the account container, create an account without a secret key.
     */
    WalletCancelBuilder.prototype.makeSender = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.clearSender(false)];
                    case 1:
                        _a.sent();
                        this._utxos
                            .filter(function (m) { return m.lock_type === Lock_1.LockType.Key; })
                            .map(function (u) { return new KeyPair_1.PublicKey(Buffer.from(u.lock_bytes, "base64")); })
                            .forEach(function (address) {
                            if (!_this.senders.exist(address)) {
                                var account = _this._accounts.findByPublicKey(address);
                                if (account === undefined)
                                    account = _this._accounts.add(WalletUtil_1.WalletUtils.getShortAddress(address), address, true, false);
                                if (account !== undefined)
                                    _this.addSender(account, Amount_1.Amount.make(0));
                            }
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    WalletCancelBuilder.prototype.calculate = function (already_changed) {
        if (already_changed === void 0) { already_changed = false; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/];
            });
        });
    };
    /**
     * Set the payload
     * @deprecated
     */
    WalletCancelBuilder.prototype.setPayload = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/];
            });
        });
    };
    Object.defineProperty(WalletCancelBuilder.prototype, "tx", {
        get: function () {
            return this._tx;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WalletCancelBuilder.prototype, "payload", {
        /**
         * The payload of a transaction
         */
        get: function () {
            return Buffer.alloc(0);
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Clear all data
     * @param is_dispatch
     */
    WalletCancelBuilder.prototype.clear = function (is_dispatch) {
        if (is_dispatch === void 0) { is_dispatch = true; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.clearReceiver(is_dispatch)];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.clearSender(is_dispatch)];
                    case 2:
                        _a.sent();
                        this._payload = Buffer.alloc(0);
                        this._tx = undefined;
                        this._utxos.length = 0;
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Determine whether the set transaction is a cancellation transaction.
     * If it is made for cancellation of another transaction, return true. Otherwise, return false.
     */
    WalletCancelBuilder.prototype.isCancelTx = function () {
        if (this._tx !== undefined) {
            var _loop_3 = function (sender) {
                if (this_3._tx.outputs.find(function (output) { return KeyPair_1.PublicKey.equal(output.address, sender.account.address); }) ===
                    undefined)
                    return { value: false };
            };
            var this_3 = this;
            for (var _i = 0, _a = this.senders.items; _i < _a.length; _i++) {
                var sender = _a[_i];
                var state_1 = _loop_3(sender);
                if (typeof state_1 === "object")
                    return state_1.value;
            }
            var _loop_4 = function (output) {
                if (this_4.senders.items.find(function (sender) { return KeyPair_1.PublicKey.equal(output.address, sender.account.address); }) ===
                    undefined)
                    return { value: false };
            };
            var this_4 = this;
            for (var _b = 0, _c = this._tx.outputs; _b < _c.length; _b++) {
                var output = _c[_b];
                var state_2 = _loop_4(output);
                if (typeof state_2 === "object")
                    return state_2.value;
            }
        }
        else {
            return false;
        }
        return true;
    };
    /**
     * Check if there is a condition to create a transaction.
     */
    WalletCancelBuilder.prototype.validate = function () {
        if (this._tx === undefined)
            return {
                code: Types_1.WalletResultCode.Cancel_NotAssignedTx,
                message: Types_1.WalletMessage.Cancel_NotAssignedTx,
            };
        var key_pairs = this.senders.items.map(function (m) {
            return new KeyPair_1.KeyPair(m.account.address, m.account.secret !== undefined ? m.account.secret : new KeyPair_1.SecretKey(ECC_1.Scalar.random()));
        });
        // Create a cancellation transaction.
        var canceller = new TxCanceller_1.TxCanceller(this._tx, this._utxos, key_pairs);
        var res = canceller.build();
        // Check for errors that occurred during the cancellation transaction creation process.
        switch (res.code) {
            case TxCanceller_1.TxCancelResultCode.Cancel_NotAllowUnfreezing:
                return {
                    code: Types_1.WalletResultCode.Cancel_NotAllowUnfreezing,
                    message: Types_1.WalletMessage.Cancel_NotAllowUnfreezing,
                };
            case TxCanceller_1.TxCancelResultCode.Cancel_InvalidTransaction:
                return {
                    code: Types_1.WalletResultCode.Cancel_InvalidTransaction,
                    message: Types_1.WalletMessage.Cancel_InvalidTransaction,
                };
            case TxCanceller_1.TxCancelResultCode.Cancel_NotFoundUTXO:
                return {
                    code: Types_1.WalletResultCode.Cancel_NotFoundUTXO,
                    message: Types_1.WalletMessage.Cancel_NotFoundUTXO,
                };
            case TxCanceller_1.TxCancelResultCode.Cancel_UnsupportedLockType:
                return {
                    code: Types_1.WalletResultCode.Cancel_UnsupportedLockType,
                    message: Types_1.WalletMessage.Cancel_UnsupportedLockType,
                };
            case TxCanceller_1.TxCancelResultCode.Cancel_NotFoundKey:
                return {
                    code: Types_1.WalletResultCode.Cancel_NotFoundKey,
                    message: Types_1.WalletMessage.Cancel_NotFoundKey,
                };
            case TxCanceller_1.TxCancelResultCode.Cancel_NotEnoughFee:
                return {
                    code: Types_1.WalletResultCode.Cancel_NotEnoughFee,
                    message: Types_1.WalletMessage.Cancel_NotEnoughFee,
                };
            case TxCanceller_1.TxCancelResultCode.FailedBuildTransaction:
                return {
                    code: Types_1.WalletResultCode.FailedBuildTransaction,
                    message: Types_1.WalletMessage.FailedBuildTransaction,
                };
        }
        return {
            code: Types_1.WalletResultCode.Success,
            message: Types_1.WalletMessage.Success,
        };
    };
    /**
     * Build a transaction.
     */
    WalletCancelBuilder.prototype.buildTransaction = function () {
        if (this._tx === undefined)
            return {
                code: Types_1.WalletResultCode.Cancel_NotAssignedTx,
                message: Types_1.WalletMessage.Cancel_NotAssignedTx,
            };
        var res_valid = this.validate();
        if (res_valid.code !== Types_1.WalletResultCode.Success)
            return res_valid;
        if (this.getReadOnlyAccount().length > 0) {
            return { code: Types_1.WalletResultCode.ExistUnknownSecretKey, message: Types_1.WalletMessage.ExistUnknownSecretKey };
        }
        try {
            var key_pairs = [];
            for (var _i = 0, _a = this.senders.items; _i < _a.length; _i++) {
                var sender = _a[_i];
                if (sender.account.secret !== undefined)
                    key_pairs.push(KeyPair_1.KeyPair.fromSeed(sender.account.secret));
            }
            // Create a cancellation transaction.
            var canceller = new TxCanceller_1.TxCanceller(this._tx, this._utxos, key_pairs);
            var res = canceller.build();
            // If there are no errors, send
            if (res.code === TxCanceller_1.TxCancelResultCode.Success && res.tx !== undefined) {
                return {
                    code: Types_1.WalletResultCode.Success,
                    message: Types_1.WalletMessage.Success,
                    data: res.tx,
                };
            }
            else {
                return {
                    code: Types_1.WalletResultCode.UnknownError,
                    message: Types_1.WalletMessage.UnknownError,
                };
            }
        }
        catch (e) {
            return { code: Types_1.WalletResultCode.FailedBuildTransaction, message: Types_1.WalletMessage.FailedBuildTransaction };
        }
    };
    /**
     * Get the overview of the transaction built
     */
    WalletCancelBuilder.prototype.getTransactionOverview = function () {
        var res = this.buildTransaction();
        if (res.code !== Types_1.WalletResultCode.Success || res.data === undefined)
            return {
                code: res.code,
                message: res.message,
            };
        var tx = res.data;
        var tx_hash = Hash_1.hashFull(tx);
        var r = [];
        for (var idx = 0; idx < tx.outputs.length; idx++) {
            r.push({
                utxo: Hash_1.makeUTXOKey(tx_hash, jsbi_1.default.BigInt(idx)),
                address: tx.outputs[idx].address,
                amount: tx.outputs[idx].value,
            });
        }
        var s = [];
        var _loop_5 = function (input) {
            var found = this_5._utxos.find(function (m) { return Hash_1.Hash.equal(m.utxo, input.utxo); });
            if (found !== undefined) {
                s.push({
                    utxo: found.utxo,
                    address: new KeyPair_1.PublicKey(Buffer.from(found.lock_bytes, "base64")),
                    amount: found.amount,
                });
            }
        };
        var this_5 = this;
        for (var _i = 0, _a = tx.inputs; _i < _a.length; _i++) {
            var input = _a[_i];
            _loop_5(input);
        }
        var sum_s = s.reduce(function (prev, value) { return Amount_1.Amount.add(prev, value.amount); }, Amount_1.Amount.make(0));
        var sum_r = r.reduce(function (prev, value) { return Amount_1.Amount.add(prev, value.amount); }, Amount_1.Amount.make(0));
        var fee = Amount_1.Amount.subtract(sum_s, sum_r);
        return {
            code: Types_1.WalletResultCode.Success,
            message: Types_1.WalletMessage.Success,
            data: {
                receivers: r,
                senders: s,
                payload: this.payload,
                fee_tx: fee,
                fee_payload: this.fee_payload,
                fee_freezing: Amount_1.Amount.make(0),
            },
        };
    };
    /**
     * Get the overview of the original transaction
     */
    WalletCancelBuilder.prototype.getOriginalTransactionOverview = function () {
        if (this._tx === undefined) {
            return {
                code: Types_1.WalletResultCode.Cancel_NotAssignedTx,
                message: Types_1.WalletMessage.Cancel_NotAssignedTx,
            };
        }
        var tx = this._tx;
        var tx_hash = Hash_1.hashFull(tx);
        var r = [];
        for (var idx = 0; idx < tx.outputs.length; idx++) {
            r.push({
                utxo: Hash_1.makeUTXOKey(tx_hash, jsbi_1.default.BigInt(idx)),
                address: tx.outputs[idx].address,
                amount: tx.outputs[idx].value,
            });
        }
        var s = [];
        var _loop_6 = function (input) {
            var found = this_6._utxos.find(function (m) { return Hash_1.Hash.equal(m.utxo, input.utxo); });
            if (found !== undefined) {
                s.push({
                    utxo: found.utxo,
                    address: new KeyPair_1.PublicKey(Buffer.from(found.lock_bytes, "base64")),
                    amount: found.amount,
                });
            }
        };
        var this_6 = this;
        for (var _i = 0, _a = tx.inputs; _i < _a.length; _i++) {
            var input = _a[_i];
            _loop_6(input);
        }
        var sum_s = s.reduce(function (prev, value) { return Amount_1.Amount.add(prev, value.amount); }, Amount_1.Amount.make(0));
        var sum_r = r.reduce(function (prev, value) { return Amount_1.Amount.add(prev, value.amount); }, Amount_1.Amount.make(0));
        var fee = Amount_1.Amount.subtract(sum_s, sum_r);
        var fee_payload = TxPayloadFee_1.TxPayloadFee.getFeeAmount(this._tx.payload.length);
        var fee_freezing = tx.isFreeze() ? Constant_1.Constant.SlashPenaltyAmount : Amount_1.Amount.make(0);
        var fee_tx;
        if (Amount_1.Amount.greaterThanOrEqual(fee, fee_payload)) {
            var fee_tx_freezing = Amount_1.Amount.subtract(fee, fee_payload);
            if (Amount_1.Amount.greaterThanOrEqual(fee_tx_freezing, fee_freezing)) {
                fee_tx = Amount_1.Amount.subtract(fee_tx_freezing, fee_freezing);
            }
            else {
                fee_tx = Amount_1.Amount.make(0);
            }
        }
        else {
            fee_tx = Amount_1.Amount.make(0);
        }
        return {
            code: Types_1.WalletResultCode.Success,
            message: Types_1.WalletMessage.Success,
            data: {
                receivers: r,
                senders: s,
                payload: this._tx.payload,
                fee_tx: fee_tx,
                fee_payload: fee_payload,
                fee_freezing: fee_freezing,
            },
        };
    };
    return WalletCancelBuilder;
}(WalletTxBuilder));
exports.WalletCancelBuilder = WalletCancelBuilder;
//# sourceMappingURL=WalletTxBuilder.js.map